import React, { FC, useState, useEffect, useMemo, useCallback, Dispatch, SetStateAction } from 'react';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useFormik } from 'formik';

import { reduxActions, useDispatch } from 'store';
import { useGetCmsSectionFields, useGetCmsArticleCountries, useGetCmsLayouts } from 'api';
import {
  ICmsSectionItem,
  ICmsArticleRequest,
  ICmsArticleResponse,
  ICmsArticleCountryListItem,
  ICmsLayoutsItem
} from 'api/types';

import { ArrowLeft } from 'react-bootstrap-icons';
import {
  Input,
  Button,
  FormElement,
  Checkbox,
  DateRangePicker,
  TranslatedInput,
  TranslatedEditor,
  TranslatedFile
} from 'components/controls';
import WiseSelect from 'components/wise/WiseSelect';

import styles from 'theme/pages/Cms/components/ArticleForm.module.scss';

interface IQueryParam {
  language: string;
}

interface IProps {
  sectionList: ICmsSectionItem[];
  isLoading: boolean;
  mutate: (arg: ICmsArticleRequest) => void;
  data?: ICmsArticleResponse;
  setQueryParams?: Dispatch<SetStateAction<IQueryParam>>;
}

const ArticleForm: FC<IProps> = ({ sectionList, isLoading, mutate, data, setQueryParams }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [selectedSection, setSelectedSection] = useState<number>(0);
  const [selectedCountry, setSelectedCountry] = useState<string>('pl');

  const { data: sectionFieldsData, refetch: refetchSectionFieldsData } = useGetCmsSectionFields(
    selectedSection,
    {},
    { enabled: false }
  );

  const { data: articleCountries } = useGetCmsArticleCountries();
  const { data: cmsLayouts } = useGetCmsLayouts();

  useEffect(() => {
    dispatch(
      reduxActions.setBreadcrumbs([
        { name: t('Dashboard'), path: '/dashboard' },
        { name: t('Lista artykułów'), path: '/dashboard/cms/articles' },
        {
          name:
            data?.title.find((o) => o.language === selectedCountry)?.translation ||
            t('Utwórz artykuł'),
          path: undefined
        }
      ])
    );
  }, [data, selectedCountry]);

  useEffect(() => {
    if (selectedSection) {
      refetchSectionFieldsData();
    }
  }, [selectedSection]);

  useEffect(() => {
    if (data) {
      setSelectedSection(data.section_id);
    }
  }, [data]);

  useEffect(() => {
    if (articleCountries?.items) {
      setSelectedCountry(articleCountries.items[0].code);
    }
  }, [articleCountries]);

  useEffect(() => {
    if (selectedCountry && setQueryParams) {
      setQueryParams({ language: selectedCountry });
    }
  }, [selectedCountry]);

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      section_id: data?.section_id,
      symbol: data?.symbol || '',
      title: data?.title || null,
      language: data?.language || articleCountries?.items[0].code || '',
      from_date: data?.from_date || null,
      to_date: data?.to_date || null,
      is_active: data?.is_active || false,
      article_fields: data?.article_fields || [],
      layouts: data?.layouts || null
    },
    onSubmit: async (values) => {
      await mutate(values);
    },
    validateOnChange: false,
    enableReinitialize: true
  });

  useEffect(() => {
    if (sectionFieldsData) {
      const fields = sectionFieldsData.items.map((item) => ({
        section_field_id: item.id,
        type: item.type,
        symbol: item.symbol,
        value: [],
        file: []
      }));

      setFieldValue(
        'article_fields',
        selectedSection === data?.section_id ? data?.article_fields : fields
      );
    }
  }, [sectionFieldsData]);

  const selectOptions = useMemo(
    () =>
      sectionList.map((section) => ({
        value: section.id,
        label: <span>{section.name}</span>,
        item: section
      })),
    [sectionList]
  );

  const countryOptions = useMemo(
    () =>
      (articleCountries?.items || []).map((country) => ({
        value: country.code,
        label: <span>{country.name}</span>,
        item: country
      })),
    [articleCountries]
  );

  const layoutOptions = useMemo(
    () =>
      (cmsLayouts?.items || []).map((layout) => ({
        value: layout.name,
        label: <span>{layout.name}</span>,
        item: { ...layout, value: layout.name }
      })),
    [cmsLayouts]
  );

  const getFieldTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'BOOL': return styles.typeBool;
      case 'STRING': return styles.typeString;
      case 'HTMLBLOCK': return styles.typeHtml;
      case 'IMAGE': return styles.typeImage;
      case 'FILE': return styles.typeFile;
      case 'INTEGER':
      case 'DECIMAL': return styles.typeNumber;
      default: return styles.typeDefault;
    }
  };

  const renderArticleField = useCallback(
    (type: string, sectionFieldId: number, index: number) => {
      const field = values.article_fields.find((f) => f.section_field_id === sectionFieldId);
      const countries = articleCountries?.items || [];

      switch (type) {
        case 'STRING':
          return (
            <TranslatedInput
              name={`article_fields[${index}].value`}
              value={field?.value || []}
              selectedCountry={selectedCountry}
              setFieldValue={setFieldValue}
              countries={countries}
            />
          );
        case 'HTMLBLOCK':
          return (
            <TranslatedEditor
              name={`article_fields[${index}].value`}
              value={field?.value || []}
              selectedCountry={selectedCountry}
              setFieldValue={setFieldValue}
              countries={countries}
            />
          );
        case 'IMAGE':
        case 'FILE':
          return (
            <TranslatedFile
              name={`article_fields[${index}].file`}
              file={field?.file || []}
              selectedCountry={selectedCountry}
              setFieldValue={setFieldValue}
              countries={countries}
            />
          );
        case 'BOOL':
          return (
            <div className={styles.boolWrapper}>
              <Checkbox
                checked={field?.value === 'true'}
                onClick={() =>
                  setFieldValue(
                    `article_fields[${index}].value`,
                    field?.value === 'true' ? 'false' : 'true'
                  )
                }
              />
              <span className={styles.boolLabel}>
                {field?.value === 'true' ? t('Tak') : t('Nie')}
              </span>
            </div>
          );
        case 'INTEGER':
          return (
            <input
              className={styles.numberInput}
              type="number"
              step={1}
              value={field?.value || ''}
              onChange={({ target: { value } }) =>
                setFieldValue(`article_fields[${index}].value`, value)
              }
            />
          );
        case 'DECIMAL':
          return (
            <input
              className={styles.numberInput}
              type="number"
              step="any"
              value={field?.value || ''}
              onChange={({ target: { value } }) =>
                setFieldValue(`article_fields[${index}].value`, value)
              }
            />
          );
        default:
          return null;
      }
    },
    [values.article_fields, selectedCountry, articleCountries, setFieldValue]
  );

  const articleFields = useMemo(() => {
    const sourceFields = data?.article_fields
      ? data.article_fields.map((field) => ({
          id: field.section_field_id,
          type: field.type,
          label: sectionFieldsData?.items.find((f) => f.id === field.section_field_id)?.label || field.symbol,
          description: sectionFieldsData?.items.find((f) => f.id === field.section_field_id)?.description
        }))
      : (sectionFieldsData?.items || []).map((field) => ({
          id: field.id,
          type: field.type,
          label: field.label,
          description: field.description
        }));

    if (sourceFields.length === 0) {
      return null;
    }

    return sourceFields.map((field, index) => (
      <div key={field.id} className={styles.fieldCard}>
        <div className={styles.fieldHeader}>
          <span className={styles.fieldIndex}>{index + 1}</span>
          <div className={styles.fieldMeta}>
            <span className={styles.fieldLabel}>{field.label}</span>
            {field.description && (
              <span className={styles.fieldDescription}>{field.description}</span>
            )}
          </div>
          <span className={classnames(styles.fieldType, getFieldTypeBadgeClass(field.type))}>{field.type}</span>
        </div>
        <div className={styles.fieldControl}>
          {renderArticleField(field.type, field.id, index)}
        </div>
      </div>
    ));
  }, [sectionFieldsData, data, values, selectedCountry, renderArticleField]);

  const pageTitle = data
    ? `${t('Edytuj artykuł')}${data.title.find((o) => o.language === selectedCountry)?.translation ? ' - ' + data.title.find((o) => o.language === selectedCountry)?.translation : ''}`
    : t('Utwórz artykuł');

  return (
    <div className={classnames(styles.componentWrapper, 'StylePath-Pages-Cms-components-ArticleForm')}>
      <div className={styles.pageHeader}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={() => navigate(data?.section_id ? `/managment_panel/cms/articles?section_id=${data.section_id}` : '/managment_panel/cms/articles')}
          title={t('Wróć')}>
          <ArrowLeft size={16} />
        </button>
        <div className={styles.text}>{pageTitle}</div>
      </div>

      <form onSubmit={handleSubmit} noValidate>
        {/* Sekcja: Dane podstawowe */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>
            <Trans>Dane podstawowe</Trans>
          </h2>
          <div className={styles.cardBody}>
          <div className={styles.row}>
            <FormElement halfWidth>
              <label><Trans>Sekcja</Trans></label>
              <WiseSelect<ICmsSectionItem>
                options={selectOptions}
                variant="default"
                value={values.section_id}
                disabled={!!data}
                onChange={(item) => {
                  setFieldValue('section_id', item?.id);
                  setSelectedSection(item?.id || 0);
                }}
              />
            </FormElement>

            <FormElement halfWidth>
              <label><Trans>Język</Trans></label>
              <WiseSelect<ICmsArticleCountryListItem>
                options={countryOptions}
                variant="default"
                value={selectedCountry}
                onChange={(item) => {
                  if (!Array.isArray(item) && item?.code) {
                    setSelectedCountry(item.code);
                    setFieldValue('language', item.code);
                  }
                }}
              />
            </FormElement>
          </div>

          <div className={styles.row}>
            <FormElement halfWidth>
              <label><Trans>Symbol</Trans></label>
              <Input
                value={values.symbol}
                onChange={(value) => setFieldValue('symbol', value)}
                error={errors.symbol}
              />
            </FormElement>

            <FormElement halfWidth>
              <label><Trans>Nazwa</Trans></label>
              <TranslatedInput
                name="title"
                value={values.title}
                selectedCountry={selectedCountry}
                setFieldValue={setFieldValue}
                countries={articleCountries?.items || []}
              />
            </FormElement>
          </div>
          </div>
        </section>

        {/* Sekcja: Ustawienia wyświetlania */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>
            <Trans>Ustawienia wyświetlania</Trans>
          </h2>
          <div className={styles.cardBody}>
          <div className={styles.row}>
            <FormElement halfWidth>
              <label>Layout</label>
              <WiseSelect<ICmsLayoutsItem>
                options={layoutOptions}
                variant="default"
                value={values.layouts || undefined}
                disabled={!cmsLayouts}
                clearable
                mode="multichoice"
                onChange={(items) => {
                  const layoutValue =
                    Array.isArray(items) &&
                    items
                      ?.map((item) => item.item.name)
                      .toString()
                      .replace(',', '|');
                  setFieldValue('layouts', layoutValue);
                }}
                error={errors.layouts}
              />
            </FormElement>

            <FormElement halfWidth>
              <label><Trans>Aktywny</Trans></label>
              <div className={styles.activeWrapper}>
                <Checkbox
                  checked={values.is_active}
                  onClick={() => setFieldValue('is_active', !values.is_active)}
                />
              </div>
            </FormElement>
          </div>

          <div className={styles.row}>
            <FormElement halfWidth>
              <label><Trans>Zakres dat w których artykuł ma się wyświetlać:</Trans></label>
              <div className={styles.dateWrapper}>
                <DateRangePicker
                  dateRange={{
                    from: values.from_date || undefined,
                    to: values.to_date || undefined
                  }}
                  onChange={(range) => {
                    setFieldValue('from_date', format(new Date(range.from), 'yyyy-MM-dd'));
                    setFieldValue('to_date', format(new Date(range.to), 'yyyy-MM-dd'));
                  }}
                />
                <Button
                  color="gray"
                  onClick={() => {
                    setFieldValue('from_date', null);
                    setFieldValue('to_date', null);
                  }}>
                  {t('Wyczyść daty')}
                </Button>
              </div>
            </FormElement>
          </div>
          </div>
        </section>

        {/* Sekcja: Pola artykułu */}
        <section className={styles.card}>
          <h2 className={styles.cardTitle}>
            <Trans>Pola artykułu</Trans>
          </h2>
          <div className={styles.cardBody}>
          {!values.section_id ? (
            <p className={styles.emptyState}>
              <Trans>Wybierz sekcję, aby zobaczyć pola artykułu.</Trans>
            </p>
          ) : articleFields === null || (Array.isArray(articleFields) && articleFields.length === 0) ? (
            <p className={styles.emptyState}>
              <Trans>Ta sekcja nie ma zdefiniowanych pól.</Trans>
            </p>
          ) : (
            <div className={styles.fieldsGrid}>{articleFields}</div>
          )}
          </div>
        </section>

        <div className={styles.actions}>
          <Button color="gray" onClick={() => navigate('/managment_panel/cms/articles')}>
            <Trans>Anuluj</Trans>
          </Button>
          <Button color="primary" htmlType="submit" loading={isLoading}>
            <Trans>Zapisz</Trans>
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ArticleForm;
